/**
 * ⭐⭐ `R2-UI8` · **El popup «Eliminar respuesta automática».** `// ADR-0348`
 * `R2-F3-CA-36`…`CA-39`, `PRD_R2.md:1145-1148`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ① POR QUÉ NO ES UN `confirm()` NATIVO, Y NO ES UNA PREFERENCIA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Un `window.confirm()` mide una línea y deja **dos criterios inalcanzables**:
 *
 *   · `CA-37` pide que el popup del botón de baja diga **otro aviso**. Un
 *     `confirm()` lleva una cadena y ningún formato: el aviso largo entraría como
 *     un párrafo suelto sin jerarquía, y no hay forma de afirmar que esté.
 *   · `CA-38` pide que **Escape no elimine**. El `confirm()` nativo no se puede
 *     montar en `happy-dom` ni observar desde un examen: «Escape no eliminó»
 *     sería cierto por construcción —nada se ejecutó— y no mediría nada.
 *
 * ⇒ `Dialogo` (`../../banco-de-contenido/dialogo.tsx`), que además encierra el
 * foco y lo devuelve al botón que lo abrió.
 *
 * ⛔ **Escape NO se escribe acá**: lo da `Dialogo`, y las dos salidas —«Cancelar»
 * y la tecla— llaman al MISMO `onCancelar`. Un segundo manejador serían dos
 * caminos que pueden divergir.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ② LOS DOS AVISOS SON EXCLUYENTES, Y LA PALABRA ES «EN LUGAR DE»
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `CA-37`, textual: *«el popup dice […] **en lugar** del aviso común»*. No además.
 *
 * ⛔ **Mostrar los dos es la salida cómoda y está mal**: el aviso común dice *«el
 * toque va a quedar registrado igual»* —que es tranquilizador— y el de la baja
 * dice que **la gente que pidió salir va a seguir recibiendo envíos**. Leídos
 * juntos, el primero desactiva al segundo, que es exactamente el mensaje que no
 * se puede perder.
 *
 * ⚠️ Se decide por `accion === "opt_out"` —el valor del contrato, nombrado UNA
 * vez en `borde-botones.ts`— y **nunca por el texto del botón**: «Quiero salir
 * del programa» es el dato de una instancia, no la regla.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ③ EL TEXTO DEL BOTÓN SE INTERPOLA ACÁ Y NO VIAJA AL SERVIDOR
 * ════════════════════════════════════════════════════════════════════════════
 *
 * El cuerpo nombra el botón (`CA-36`) porque una confirmación sobre «la
 * respuesta» con cincuenta filas a la vista no dice sobre cuál. Es lo mismo que
 * hace `popup-quitar.tsx`, y es distinto del CUERPO DE ERROR del borde, que
 * `ADR-0201` obliga a mantener fijo y sin ningún valor del pedido: allá sería una
 * fuga por eco, acá es un dato que el navegador ya tiene dibujado en la fila.
 */

import type { JSX } from "react"
import { Dialogo } from "../../banco-de-contenido/dialogo.tsx"
import { ACCION_DE_BAJA } from "./borde-botones.ts"
import type { FilaDeRespuesta } from "./borde-botones.ts"

// ⟦rastro · ADR-0082 · R4-UIT1 · 4/10/2026⟧ Los textos viejos de estos dos avisos («… KAI no va
// a contestarle …» y «… KAI no va a contestar …», PRD_R4.md:1986-1987) se conservan acá en
// comentario; el texto visible pasó a «el chatbot».

/** `CA-36` · el aviso común, palabra por palabra (`PRD_R2.md:1147`). */
export function avisoComun(sello: string): string {
  return `Afecta solo a ${sello}: si un participante aprieta ese botón, el chatbot no va a contestarle y el toque va a quedar registrado igual.`
}

/**
 * `CA-37` · el aviso del botón de baja, palabra por palabra (`PRD_R2.md:1148`).
 *
 * ⛔ **No nombra al brazo**, al revés que el común: lo que describe no es un
 * alcance —a quién afecta— sino una consecuencia. Agregarle el sello lo haría
 * sonar acotado, que es lo contrario de lo que dice.
 */
export const AVISO_DE_LA_BAJA =
  "Si eliminas esta respuesta, apretar ese botón deja de dar de baja a nadie: el chatbot no va a contestar y el participante va a seguir recibiendo envíos."

export function PopupEliminar({
  fila,
  sello,
  enCurso,
  error,
  onCancelar,
  onConfirmar,
}: {
  readonly fila: FilaDeRespuesta
  /** El nombre del brazo, ya armado («KAI · Brazo Híbrido»). */
  readonly sello: string
  readonly enCurso: boolean
  readonly error: string | null
  readonly onCancelar: () => void
  readonly onConfirmar: () => void
}): JSX.Element {
  const esDeBaja = fila.action === ACCION_DE_BAJA

  return (
    <Dialogo titulo="Eliminar respuesta automática" onCerrar={onCancelar}>
      <p>Se elimina la respuesta al botón «{fila.buttonText}».</p>
      {/* ⛔ Ver el bloque ②: uno **en lugar** del otro, nunca los dos. */}
      <p>{esDeBaja ? AVISO_DE_LA_BAJA : avisoComun(sello)}</p>
      {/*
        ⚠️ El fallo del `DELETE` se dice ADENTRO del popup y el popup NO se
        cierra. Cerrarlo dejaría al operador mirando una lista donde la fila sigue
        estando, sin saber si eso es «no se eliminó» o «se eliminó y la tabla está
        vieja».
      */}
      {error !== null && (
        <p className="plantillas__error" role="alert">
          {error}
        </p>
      )}
      <footer className="dialogo__acciones">
        <button type="button" className="boton" onClick={onCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          /*
           * ⭐ `R1.1-F5-CA-95` · el confirmar de una acción destructiva va SÓLIDO
           * en `--peligro`. El «Eliminar» de la FILA, que sólo abre esto, se queda
           * en la variante callada: abrir una confirmación no es destructivo.
           */
          className="boton boton--peligro-solido"
          aria-busy={enCurso}
          disabled={enCurso}
          onClick={onConfirmar}
        >
          Sí, eliminar respuesta
        </button>
      </footer>
    </Dialogo>
  )
}
